import styled from 'styled-components'

import type { BadgeVariant, IconRegistry } from '@percent/lemonade'
import { Avatar, Badge, Flex } from '@percent/lemonade'

import { useOrganisationProfileContext } from '../../context/OrganisationProfileContext'
import { useTranslation } from '../../hooks/useTranslation'
import {
  OrganisationProfile,
  OrganisationVerificationStatus,
} from '../../types/organisationProfile.types'
import { ProfileLink } from '../ProfileLink/ProfileLink'
import { DISPLAY_FONT } from '../SectionTitle/SectionTitle'

const Band = styled.div<{ $image?: string }>`
  ${({ theme, $image }) => `
    height: calc(${theme.spacing.xxl} * 6);
    background-color: ${theme.semantic.backgroundBrandSubtle};
    ${$image ? `background: url("${$image}") center / cover no-repeat;` : ''}
  `}
`

// Pulls the logo up so it straddles the band's bottom edge.
const Identity = styled(Flex)`
  ${({ theme }) => `
    margin-top: calc(${theme.spacing.xxl} * -1);
    padding: 0 ${theme.spacing.l};
  `}
`

const OrganisationName = styled.h1`
  ${({ theme }) => `
    margin: 0;
    text-align: center;
    font-family: ${DISPLAY_FONT};
    font-size: ${theme.typographyTokens.h1.fontSize};
    line-height: ${theme.typographyTokens.h1.lineHeight};
    color: ${theme.semantic.contentPrimary};
  `}
`

type VerificationBadgeProps = {
  variant: BadgeVariant
  icon?: keyof typeof IconRegistry
  text: string
}

export type OrganisationProfileHeaderProps = {
  profile: OrganisationProfile
}

export function OrganisationProfileHeader({
  profile,
}: Readonly<OrganisationProfileHeaderProps>) {
  const { t } = useTranslation()
  const { links } = useOrganisationProfileContext()
  const { organisation, verification } = profile
  const name = organisation.displayName || organisation.name

  const verificationBadge = (
    status: OrganisationVerificationStatus | undefined
  ): VerificationBadgeProps => {
    switch (status) {
      case 'VERIFIED':
        return {
          variant: 'interactive',
          icon: 'check-badge',
          text: t('profile.verificationStatus.verified'),
        }
      case 'PENDING':
        return {
          variant: 'default',
          icon: 'clock',
          text: t('profile.verificationStatus.pending'),
        }
      case 'REJECTED':
        return {
          variant: 'critical',
          icon: 'reject',
          text: t('profile.verificationStatus.rejected'),
        }
      default:
        return {
          variant: 'default',
          text: t('profile.verificationStatus.unknown'),
        }
    }
  }

  const badge = verificationBadge(verification?.status)

  return (
    <>
      <Band $image={organisation.images[0]} />
      <Identity direction="column" align="center" gap={12}>
        <Avatar image={organisation.logo} alt={name} size="xlarge" border />
        <OrganisationName>{name}</OrganisationName>
        <Flex gap={8} wrap="wrap" justify="center">
          <Badge variant={badge.variant} icon={badge.icon}>
            {badge.text}
          </Badge>
          {organisation.claimedAt && (
            <Badge variant="default" icon="user-circle">
              {t('profile.claimed')}
            </Badge>
          )}
        </Flex>
        {verification && links.verification && (
          <ProfileLink
            href={links.verification(verification.validationSubmissionId)}
          >
            {t('profile.viewVerification')}
          </ProfileLink>
        )}
      </Identity>
    </>
  )
}
